import type { LocalPrivateSpecialistsConfig } from './config';

/**
 * Splunk (index `search`) events of the local-private-specialists suggestion. One group, four types,
 * every event carrying the same base fields so a funnel is one query:
 *
 *   event_group = profile_local_private_specialists
 *   event_type  = profile_local_private_specialists-{view|empty|click-doctor|click-more}
 *
 * Base fields: `slug` (the profile the suggestion sits on), `placement` (`profile` = own booking
 * closed/full, `profile-open` = own booking open, `booking-full` = the «نوبت پر شده» modal), `variant`,
 * `city`, `expertise`. The Splunk client also adds `terminal_id`, `current_url` and the navigation
 * context, so a click can be joined to that visitor's later booking events.
 *
 *   view          shown_doctors (slugs, in display order), shown_count, bookable_count, candidate_count,
 *                 shown_tiers (tiered profiles: clinic | online | office per shown doctor)
 *                 tiered profiles also: viewer_tier_skips + viewer_prior_views (profile card: how many tiers
 *                 this viewer ignored before this one / how often they saw this doctor), viewer_demoted (carousel)
 *   empty         reason (`no_bookable` | `search_error`), candidate_count — the denominator for
 *                 "how often there was nobody to suggest"
 *   click-doctor  doctor (slug), position (1-based in display order), element, first_turn,
 *                 tier (tiered profiles) — which kind of suggestion patients choose
 *   click-more    target (the search page opened)
 *
 * Clicks are sent with `keepalive`: the profile navigates away right after, which would otherwise
 * cancel the request before it reaches Splunk.
 */
export const LOCAL_SPECIALISTS_EVENT_GROUP = 'profile_local_private_specialists';
export const LOCAL_SPECIALISTS_VARIANT = 'local-private-specialists';

export type LocalSpecialistsPlacement = 'profile' | 'profile-open' | 'booking-full';
export type LocalSpecialistsEventType = 'view' | 'empty' | 'click-doctor' | 'click-more';

export const buildLocalSpecialistsEvent = (
  type: LocalSpecialistsEventType,
  base: { slug: string; placement: LocalSpecialistsPlacement; config: LocalPrivateSpecialistsConfig },
  fields: Record<string, unknown> = {},
) => ({
  group: LOCAL_SPECIALISTS_EVENT_GROUP,
  type: `${LOCAL_SPECIALISTS_EVENT_GROUP}-${type}`,
  event: {
    slug: base.slug,
    placement: base.placement,
    variant: LOCAL_SPECIALISTS_VARIANT,
    city: base.config.city,
    expertise: base.config.expertiseName,
    ...fields,
  },
  ...(type.startsWith('click') && { keepalive: true }),
});
